import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import {Observable, of, ReplaySubject} from 'rxjs';
import { filter, map, switchMap, takeUntil, tap, take } from 'rxjs/operators';

import { ConfigurationStoreHandler } from '../../../../configuration/services/configuration-store-handler.service';
import { INavigationApplicationInterface, INavigationBarItemInterface, INavigationConfigurationInterface } from '@ispm/navigation/interfaces';
import { IUrlState } from '@ispm/core/interfaces/router-handler.interface';
import { InterpolateService } from '@ispm/shared/services/interpolate.service';
import { NavigationConfigService } from '@ispm/navigation/services/navigationConfig.service';
import { NodePanelConfigurationService } from '@ispm-client/modules/bpmn-modeler/service/node-panel-configuration.service';
import { RouterManagerService } from '@ispm/core/services/router-manager.service';
import { SessionStorageService } from '@ispm/shared/services/session-storage.service';
import { TestService } from '@ispm/dashboard/test/services/test.service';
import {ActivatedRoute, Router} from "@angular/router";

@Component({
  selector: 'gen-navigation-bar',
  templateUrl: './navigationBar.component.html',
  styleUrls: [ './navigationBar.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationBarComponent implements OnInit, OnDestroy, AfterViewInit {
  public chosenBrand$: Observable<string>;
  public currentApp$: Observable<INavigationApplicationInterface>;
  public defaultBrand$: Observable<string>;
  public isHidden: boolean = false;
  public navigationConfig$: Observable<INavigationConfigurationInterface>;
  public navigationItems$: Observable<Array<INavigationBarItemInterface>>;
  public showMore: boolean = false;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private itemsUrlStates: { [key: string]: IUrlState } = { };

  constructor(
    private _changeDetector: ChangeDetectorRef,
    private cdRef: ChangeDetectorRef,
    private configurationAction: ConfigurationStoreHandler,
    private interpolateService: InterpolateService,
    private navigationConfigService: NavigationConfigService,
    private nodePanelConfigurationService: NodePanelConfigurationService,
    private routerManagerService: RouterManagerService,
    private testService: TestService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    this.routerManagerService.getRoutingListenerFor('ResolveEnd').subscribe(state => {
      this.cdRef.markForCheck();
      state.approve();
    });
  }

  ngAfterViewInit(): void {
    this.chosenBrand$ = this.navigationConfigService.getCurrentAppBrandData().pipe(
      tap(() => this._changeDetector.detectChanges(),
        takeUntil( this.destroyed$))
    );
    this.defaultBrand$ = this.navigationConfigService.getCurrentApplication().pipe(
      map(app => app.defaultBrand),
      tap(() => this._changeDetector.detectChanges(),
        takeUntil( this.destroyed$))
    );

    this._changeDetector.markForCheck();
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
  }

  public ngOnInit() {
    this.navigationConfigService.getWebConfiguration()
      .pipe(
        takeUntil(this.destroyed$),
        filter((configuration: INavigationConfigurationInterface) => {
          return !!configuration && !!configuration.navigation;
        })
      )
      .subscribe(data => {
        if (data.navigation && data.navigation.selected) {
          this.navigationConfigService.setCurrentAppSelectedTab(data.navigation.selected.Navigator);
        }
      });

    const currentState = this.router?.url;
    if (currentState.indexOf('admin/settings') !== -1 || currentState.indexOf('settings/dashboard') !== -1) {
      this.isHidden = true;
    }

    this.navigationConfig$ = this.navigationConfigService.getNavigationConfig();
    this.currentApp$ = this.navigationConfigService.getCurrentApp();
    this.navigationItems$ = this.navigationConfigService.getCurrentApp().pipe(
      switchMap((app) => this.navigationConfigService.getWebConfigurationByAppId(app.id)),
      takeUntil( this.destroyed$)
    );
  }

  private goToCallback($event: INavigationBarItemInterface) {
    sessionStorage.removeItem('filters');
    this.showMore = false;
    this.navigationConfigService.setCurrentAppSelectedTab($event.identify);
    const urlArray = $event.redirectUrl.split('?');
    const dict = {};
   if (urlArray.length > 1) {
      urlArray[1].split('&').forEach(prop => {
        const [key, value] = prop.split('=');
        dict[key] = value;
      });
    }
    this.navigationConfigService.setClickedNavItem($event);
    // cleaning the url array if any fragments are there
    this.router.navigate([urlArray[0].split('#')[0]], {queryParams:dict});
  }

  public onNavigationClick($event: INavigationBarItemInterface) {
    this.nodePanelConfigurationService.isNodeContentChanged(null, this, this.goToCallback, $event);
  }

  public isItemActive(navigationItem: INavigationBarItemInterface) {
    if (!this.itemsUrlStates[navigationItem.identify]) {
      this.setNavItemUrlState(navigationItem);
    }

    const itemUrlState = this.itemsUrlStates[navigationItem.identify];
    const currentUrl = this.routerManagerService.currentPageUrl || this.router.url;
    const isStateActive: boolean = itemUrlState.name === currentUrl;

    if (isStateActive && !itemUrlState.params && !itemUrlState.dict) {
      return true;
    } else {
      return isStateActive && (itemUrlState.params || itemUrlState.dict) && currentUrl.includes(navigationItem.redirectUrl);
    }
  }

  public trackByFn(index: number) {
    return index;
  }

  private setNavItemUrlState(navItem: INavigationBarItemInterface) {
    const url = this.interpolateService.interpolate(navItem.redirectUrl, { });
    this.itemsUrlStates[navItem.identify] = this.routerManagerService.urlToState(url);
  }

  private goToState(uriState, param = { }) {
    this.router.navigate([uriState.name], {queryParams: param});
  }

  public onAppLauncherClick($event) {
    const app: INavigationApplicationInterface = $event;
    if (this.navigationConfigService.getCurrentApplicationId() !== $event.id) {
      // Clear cache was added because when switching from an application that created a test (e.g. genomics) to Provider Portal
      // and adding a new test, the workflowRequest parameter is taken form 'configuration.activeConfig.Test.id' (that is the configuration in NEW-TREATMENT-PLAN-INFO-ADD) which is pointing
      // to the last created test type (and activeConfig is an array). Therefor a test with wrong tet type is created.
      // This is a non elegant solution.
      // ToDo: Provider portal should read the test type fro other parameter
      if ($event.id === 'ISPM-ONCOLOGY-PATHWAYS-NAVIGATOR-APP') {
        // Clear cached test config from store to prevent a certain application test config to be used in another application (GEN-156320).
        this.testService.clearCache();
        this.configurationAction.clearConfiguration('Test');
      }

      sessionStorage.removeItem(SessionStorageService.CURRENT_WORKLIST_DYNAMIC_SEARCH);
      sessionStorage.removeItem(SessionStorageService.WORKLIST_SESSION_STORAGE_NAME);
    }

    this.navigationConfigService.setCurrentApp(app);
    this.navigationItems$.pipe(take(1)).subscribe((items) => {
      const firstItem: INavigationBarItemInterface = items[0];
      this.onNavigationClick(firstItem);
    });
  }
}
